import React, { Suspense, lazy, useState } from "react";
import { Sparkles, BookOpen, ArrowLeft, KeyRound, Terminal } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useDemoAwareNavigate } from "utils/demoNavigation";
import { useDemoMode } from "../../../context/DemoModeContext";
import { VibeCodingPanel } from "../VibeCoding/VibeCodingPanel";
import { EnvSelectorWithTruncation } from "../EnvSelectorWithTruncation";
import { SidebarApiKey } from "../VibeCoding/SidebarApiKey";
import RingLoader from "components/Shared/UI/Loader/RingLoader/RingLoader";

const SetupTelemetry = lazy(() =>
    import("./SetupTelemetry").then((m) => ({ default: m.SetupTelemetry }))
);

type Mode = "ai" | "manual";

const AISetupButton = ({ onClick }: { onClick: () => void }) => (
    <button
        onClick={onClick}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primaryColor bg-primaryExtremeLightColor border border-primaryLightColor rounded-lg hover:bg-primaryLightColor transition-colors"
    >
        <Sparkles className="w-4 h-4" />
        Try AI Setup
        <span className="ml-0.5 px-1.5 py-0.5 text-[10px] font-semibold bg-primaryLightColor text-primaryColor rounded-full leading-none">
            New
        </span>
    </button>
);

export function TelemetryWithAI() {
    const location = useLocation();
    const { isDemoMode } = useDemoMode();
    const [mode, setMode] = useState<Mode>(() => {
        if (isDemoMode) return "manual";
        if ((location.state as any)?.mode === "manual") return "manual";
        if ((location.state as any)?.mode === "ai") return "ai";
        return "manual";
    });
    const navigate = useDemoAwareNavigate();

    if (mode === "ai") {
        return (
            <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-primaryExtremeLightColor transition-colors duration-200">
                <div className="relative mx-auto flex h-full min-h-0 w-[70%] max-w-full flex-col bg-gray-50 shadow-sm">
                    <div className="flex min-h-0 flex-1 overflow-hidden">
                        {/* Sidebar — matches SetupTelemetry's sidebar exactly */}
                        <aside className="w-[256px] flex-shrink-0 overflow-y-auto overscroll-contain bg-gray-50">
                            <div className="px-3 sm:px-4 pt-4 pb-6">
                                <button
                                    onClick={() => navigate("/onboarding/product-analytics")}
                                    className="w-fit p-0 min-w-0 text-gray-600 hover:text-gray-900 flex-shrink-0 mb-4"
                                    aria-label="Back"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                </button>

                                {/* Environment & Api Key — identical to SetupTelemetry sidebar */}
                                <div className="mb-4 pb-2">
                                    <div className="flex items-center gap-2 mb-3">
                                        <h3 className="font-medium text-gray-600">Environment & Api Key</h3>
                                    </div>
                                    <EnvSelectorWithTruncation />
                                    <div className="mt-2">
                                        <SidebarApiKey />
                                    </div>
                                </div>

                                {/* Setup Mode nav */}
                                <div className="mt-4">
                                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 px-3">
                                        Setup Mode
                                    </h3>
                                    <button
                                        onClick={() => setMode("manual")}
                                        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors text-left"
                                    >
                                        <BookOpen className="w-4 h-4 flex-shrink-0" />
                                        Step-by-Step Setup
                                    </button>
                                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-primaryExtremeLightColor border border-primaryLightColor mt-1">
                                        <Sparkles className="w-4 h-4 text-primaryColor flex-shrink-0" />
                                        <span className="text-sm font-medium text-primaryColor">AI Prompt</span>
                                        <span className="ml-auto px-1.5 py-0.5 text-[10px] font-semibold bg-primaryLightColor text-primaryColor rounded-full leading-none">
                                            New
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </aside>

                        {/* Main content — identical padding to SetupTelemetry */}
                        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-hide px-8 pt-14 pb-28 min-w-0">
                            {/* Page header — mirrors SetupTelemetry header exactly */}
                            <div className="mb-6">
                                <div className="flex items-start space-x-4">
                                    <div className="p-3 bg-indigo-50 rounded-lg flex-shrink-0">
                                        <Terminal className="w-8 h-8 text-primaryColor" />
                                    </div>
                                    <div className="flex flex-col gap-2 flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-4">
                                            <h1 className="text-[24px] font-bold text-gray-900">Setup Telemetry</h1>
                                            <button
                                                onClick={() => setMode("manual")}
                                                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-primaryColor hover:text-primaryDarkColor hover:bg-primaryExtremeLightColor rounded-lg transition-colors whitespace-nowrap flex-shrink-0"
                                            >
                                                <BookOpen className="w-4 h-4" />
                                                Step-by-Step Setup
                                            </button>
                                        </div>
                                        <p className="text-gray-600">
                                            Use an AI coding tool to automatically instrument your codebase — no manual steps required.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Content — no extra card wrapper, matches SetupTelemetry's content area flow */}
                            <VibeCodingPanel variant="telemetry" lockedUseCase="product" onGoToSetup={() => setMode("manual")} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <Suspense fallback={<RingLoader />}>
                <SetupTelemetry headerAction={isDemoMode ? <AISetupButton onClick={() => setMode("ai")} /> : undefined} />
            </Suspense>
        </div>
    );
}
